import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, TrendingUp, TrendingDown } from 'lucide-react';
import { formatTempo } from '../utils/excelProcessor';

/**
 * Componente de tabela de atendentes focada em levantadas de mão
 */
const AtendentesTable = ({ atendentesData, showDetail }) => {
  const [sortField, setSortField] = useState('totalLevantadas');
  const [sortDirection, setSortDirection] = useState('desc');

  if (!atendentesData || atendentesData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance dos Atendentes</h3>
        <p className="text-gray-500">Nenhum dado de atendente disponível.</p>
      </div>
    );
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...atendentesData].sort((a, b) => {
    const aValue = a[sortField] || 0;
    const bValue = b[sortField] || 0;
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    }
    return bValue - aValue;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-blue-500" />
      : <ChevronDown className="h-4 w-4 text-blue-500" />;
  };

  const getPerformanceColor = (taxa) => {
    if (taxa >= 80) return 'text-green-600 bg-green-50';
    if (taxa >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-6">
        👥 Performance dos Atendentes - Levantadas de Mão
        <span className="text-sm font-normal text-gray-500 ml-2">
          ({atendentesData.length} atendentes)
        </span>
      </h3>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('nome')}
              >
                <div className="flex items-center space-x-1">
                  <span>Atendente</span>
                  <SortIcon field="nome" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('totalLevantadas')}
              >
                <div className="flex items-center space-x-1">
                  <span>Total Levantadas</span>
                  <SortIcon field="totalLevantadas" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('taxaEngajamento')}
              >
                <div className="flex items-center space-x-1">
                  <span>Taxa Engajamento do Lead</span>
                  <SortIcon field="taxaEngajamento" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('taxaResposta')}
              >
                <div className="flex items-center space-x-1">
                  <span>Taxa Resposta do Atendente</span>
                  <SortIcon field="taxaResposta" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('tempoMedioResposta')}
              >
                <div className="flex items-center space-x-1">
                  <span>Tempo Médio</span>
                  <SortIcon field="tempoMedioResposta" />
                </div>
              </th>
              
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((atendente, index) => (
              <tr key={atendente.nome} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {atendente.nome.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{atendente.nome}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 font-semibold">{atendente.totalLevantadas}</div>
                  <div className="text-xs text-gray-500">
                    {atendente.levantadasComMensagem} com mensagem
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPerformanceColor(atendente.taxaEngajamento)}`}>
                    {atendente.taxaEngajamento.toFixed(1)}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPerformanceColor(atendente.taxaResposta)}`}>
                      {atendente.taxaResposta.toFixed(2)}%
                    </span>
                    <div className="ml-2">
                      {atendente.taxaResposta >= 80 ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : atendente.taxaResposta < 60 ? (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      ) : null}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-tight">
                    <div>{atendente.levantadasRespondidas} com resposta do atendente</div>
                    <div>{atendente.levantadasNaoRespondidas} sem resposta do atendente</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatTempo(atendente.tempoMedioResposta)}</div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => showDetail && showDetail('atendente_respondidas', { atendente: atendente.nome })}
                    className="text-green-600 hover:text-green-900 inline-flex items-center"
                    title="Ver levantadas respondidas"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    <span className="text-xs">Respondidas</span>
                  </button>
                  <button
                    onClick={() => showDetail && showDetail('atendente_nao_respondidas', { atendente: atendente.nome })}
                    className="text-red-600 hover:text-red-900 inline-flex items-center"
                    title="Ver levantadas não respondidas"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    <span className="text-xs">Não Resp.</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resumo da tabela */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-semibold text-gray-700">Melhor Engajamento:</span>
            <div className="text-green-600">
              {(() => {
                if (sortedData.length === 0) return 'N/A';
                const melhor = sortedData.reduce((prev, current) => 
                  prev.taxaEngajamento > current.taxaEngajamento ? prev : current
                );
                return `${melhor.nome} (${melhor.taxaEngajamento.toFixed(1)}%)`;
              })()}
            </div>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Melhor Resposta:</span>
            <div className="text-green-600">
              {(() => {
                if (sortedData.length === 0) return 'N/A';
                const melhor = sortedData.reduce((prev, current) => 
                  prev.taxaResposta > current.taxaResposta ? prev : current
                );
                return `${melhor.nome} (${melhor.taxaResposta.toFixed(1)}%)`;
              })()}
            </div>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Mais Rápido:</span>
            <div className="text-blue-600">
              {(() => {
                const comTempo = sortedData.filter(a => a.tempoMedioResposta > 0);
                if (comTempo.length === 0) return 'N/A';
                const maisRapido = comTempo.reduce((prev, current) => 
                  prev.tempoMedioResposta < current.tempoMedioResposta ? prev : current
                );
                return `${maisRapido.nome} (${formatTempo(maisRapido.tempoMedioResposta)})`;
              })()}
            </div>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Mais Ativo:</span>
            <div className="text-purple-600">
              {(() => {
                if (sortedData.length === 0) return 'N/A';
                const maisAtivo = sortedData.reduce((prev, current) => 
                  prev.totalLevantadas > current.totalLevantadas ? prev : current
                );
                return `${maisAtivo.nome} (${maisAtivo.totalLevantadas} levantadas)`;
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtendentesTable;
